// apps/share-web/src/offline/words-resolve.ts
//
// Откуда взять слова одной песни и что при этом сделать с кэшем.
//
// Отличие от библиотеки: у страницы слов сервер РАЗЛИЧАЕТ отказы, и различие
// это содержательное (song-text-api.ts, лестница 401/403/404). «Сеть подвела»
// и «песню сняли с публикации» — разные вещи, и кэш обязан реагировать
// по-разному:
//
//   network  → сеть подвела, слова остаются. Это офлайн, ради него всё.
//   gone     → сняли с публикации: кэш стереть, показать отказ.
//   forbidden→ вывели из команды: кэш стереть, показать отказ.
//   auth     → сессии нет; кэш НЕ трогаем. Решение владельца продукта от
//              21.09: один 401 от прокси посреди служения не имеет права
//              обнулить библиотеку, поэтому стирание осталось только явным —
//              выход из аккаунта и кнопка «стереть офлайн-данные» (задача 14).
//
// Схлопнуть gone/forbidden в network означало бы оставить снятую песню
// открывающейся с телефона навсегда — та самая дыра, ради которой кэш
// /api/** был запрещён в sw.js.
//
// Стирание идёт через `store.delWords`, то есть по ТОЧНОМУ ключу. Черновик
// плана отбирал ключи из `cachedKeys()` по `endsWith(songTextId)` — так
// вместе с 's1' уходила бы живая 'xs1'; замок на это стоит в
// __tests__/offline-store.test.ts.

import type { OfflineStore } from './offline-store.js';
import type { TeamLibrary } from '../library.js';

/** Те же четыре причины, что у `song-text-api.ts`. Второй копии тут нет: этот
 *  союз — вход модуля, и сужать или расширять его вправе только тот слой. */
export type WordsFetchReason = 'auth' | 'forbidden' | 'gone' | 'network';

export type WordsFetchResult<P = unknown> =
  | { ok: true; payload: P; updatedAt: string }
  | { ok: false; reason: WordsFetchReason };

export type WordsResolved<P = unknown> =
  | { source: 'network'; payload: P }
  | { source: 'cache'; payload: P }
  | { source: 'none'; reason: WordsFetchReason };

// ⚠️ ДЕФЕКТ 1 живой приёмки волны G (21.09, владелец продукта): «нажимаю на
// песню, и она секунд шесть лодинг крутится» — хотя слова уже лежали в
// кэше. Механизм — сложение двух ожиданий: до 3000 мс на саму навигацию
// (NAV_TIMEOUT_MS, sw.js) плюс безграничное ожидание ЭТОГО вызова, которое
// ничем не было ограничено. Обычный `fetch` не обязан отказывать быстро на
// мёртвом аплинке (зальный Wi-Fi без интернета, captive-портал) — на iOS
// зафиксированы случаи в минуты.
//
// Величина — 2000 мс, МЕНЬШЕ NAV_TIMEOUT_MS: здесь лёгкий JSON-GET
// (`/api/song-texts/:id`), а не полная навигация с оболочкой и её
// ресурсами, и вторая полная трёхсекундная пауза поверх уже отсиженной на
// шаге навигации снова сложилась бы к тем самым шести секундам, от которых
// чинится этот дефект. 2000 мс хватает нормальному медленному 3G ответить
// (обычный RTT — доли секунды), но не дают ожиданию разрастись: читатель на
// сцене видит вчерашние слова мгновенно вместо сегодняшних через пять
// секунд.
export const NETWORK_TIMEOUT_MS = 2000;

/** Тот же контракт, что у `offline/freshness.ts` и `public/sw.js`:
 *  `navigator.onLine === false` не врёт никогда (лжёт только положительный
 *  ответ — зальный Wi-Fi с мёртвым аплинком), поэтому это единственное
 *  безопасное значение по умолчанию.
 *
 *  Экспортирована: `song-words-view.ts` (дефект 2 живой приёмки волны G,
 *  `askSession`) переиспользует ЭТОТ же default, а не заводит вторую копию
 *  того же самого чтения `navigator`. */
export function defaultIsOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** Применяет ОДИН исход сети — общий код между «сеть ответила первой» и
 *  «сеть опоздала за таймер, но всё равно долетела» (см. `resolveWords`
 *  ниже): обе ветки обязаны решать судьбу кэша ОДИНАКОВО, иначе опоздавший
 *  `gone` не стёр бы кэш, который стёр бы вовремя пришедший. */
async function applyFetchResult<P>(
  store: OfflineStore, teamId: string, songTextId: string, got: WordsFetchResult<P>,
): Promise<WordsResolved<P>> {
  if (got.ok) {
    // Отказ записи (iOS `QuotaExceededError` — ровно когда телефон полон, то
    // есть когда офлайн-кэш нужнее всего) НЕ имеет права забрать у человека
    // слова, которые сеть уже принесла. Контракт `kv-backend.ts`: офлайн —
    // усиление, а не несущая часть; потребитель обязан пережить отказ
    // записи, не роняя экран. Без этого `catch` реджект долетал бы до
    // `resolveWordsForPage` необработанным, а страница слов зависала на
    // «Loading…» несмотря на успешный ответ сети.
    try {
      await store.saveWords(teamId, songTextId, { updatedAt: got.updatedAt, payload: got.payload });
    } catch { /* см. пояснение выше — сеть важнее записи в кэш */ }
    return { source: 'network', payload: got.payload };
  }

  // Сервер СКАЗАЛ, что этих слов у человека больше нет. Это не отсутствие
  // связи, и молчать про это кэшем нельзя.
  if (got.reason === 'gone' || got.reason === 'forbidden') {
    // Тот же принцип, что у записи выше: неудавшееся стирание не должно
    // мешать показать человеку честный отказ. Пусть устаревшая запись
    // ненадолго переживёт эту попытку — это лучше вечного спиннера.
    try {
      await store.delWords(teamId, songTextId);
    } catch { /* см. пояснение выше — отказ показываем в любом случае */ }
    return { source: 'none', reason: got.reason };
  }

  if (got.reason === 'network') {
    const cached = await store.loadWords(teamId, songTextId);
    // Каст, а не проверка: в кэше лежит ровно то, что положил успешный ответ
    // того же маршрута, а форма записи охраняется конвертом `SHAPE`
    // (offline-store.ts) — чужая форма приезжает сюда как `null`.
    if (cached) return { source: 'cache', payload: cached.payload as P };
  }

  return { source: 'none', reason: got.reason };
}

export interface ResolveWordsArgs<P> {
  store: OfflineStore;
  teamId: string;
  songTextId: string;
  fetchOne: () => Promise<WordsFetchResult<P>>;
  /**
   * «Браузер считает себя офлайн?» — внедряемый параметр, а не прямое чтение
   * `navigator` внутри модуля: вокруг `resolveWords` уже стоит дюжина
   * тестов, держащих его чистой функцией среды (ни DOM, ни браузерных
   * глобалов), и вплетать сюда `navigator` напрямую значило бы сломать
   * именно это свойство. Не задано — `navigator.onLine === false` (см.
   * `defaultIsOffline` выше).
   */
  isOffline?: () => boolean;
}

export async function resolveWords<P>(args: ResolveWordsArgs<P>): Promise<WordsResolved<P>> {
  const isOffline = args.isOffline ?? defaultIsOffline;

  // Браузер САМ сказал, что сети нет — ходить в неё означало бы жечь
  // секунды (и, возможно, остаток жизни мёртвого `fetch`) ради
  // гарантированного отказа. См. doc-комментарий `isOffline` выше.
  // ⚠️ ЗАМОК: убрать эту ветку (звать `fetchOne` и в офлайне тоже) —
  // красит тест «браузер офлайн — в сеть не ходим вовсе»
  // (words-offline.test.ts): он проверяет, что `fetchOne` НЕ был вызван, а
  // не только итоговый источник ответа.
  if (isOffline()) {
    const cached = await args.store.loadWords(args.teamId, args.songTextId);
    if (cached) return { source: 'cache', payload: cached.payload as P };
    return { source: 'none', reason: 'network' };
  }

  // Браузер СЧИТАЕТ себя онлайн — тот самый случай, когда он может врать
  // (зальный Wi-Fi с мёртвым аплинком, captive-портал): сеть идёт первой,
  // как и раньше (свежие слова важнее мгновенности, когда сеть похожа на
  // живую) — но ожидание больше не безгранично.
  const networkPromise = args.fetchOne();
  const TIMED_OUT = Symbol('resolveWords-timeout');
  let timeoutHandle: ReturnType<typeof setTimeout>;
  const timeout = new Promise<typeof TIMED_OUT>((resolve) => {
    timeoutHandle = setTimeout(() => resolve(TIMED_OUT), NETWORK_TIMEOUT_MS);
  });
  // ⚠️ ЗАМОК: убрать `clearTimeout`/сам таймер (ждать `networkPromise`
  // безусловно, как раньше) — красит тест «fetch висит навсегда» (fake
  // timers, `vi.advanceTimersByTimeAsync`): без таймера `await` внутри
  // `resolveWords` не отпускает управление, и тест падает по СВОЕМУ
  // таймауту vitest, а не по значимой ошибке.
  const winner = await Promise.race([networkPromise, timeout]);
  clearTimeout(timeoutHandle!);

  if (winner !== TIMED_OUT) {
    return applyFetchResult(args.store, args.teamId, args.songTextId, winner);
  }

  const cached = await args.store.loadWords(args.teamId, args.songTextId);
  if (cached) {
    // Опоздавший ответ (успех ИЛИ отказ — `applyFetchResult` обрабатывает
    // оба одинаково) не теряется: он приземляется в кэш/стирает его в
    // фоне, молча — до экрана он уже не доедет, страница показана кэшем.
    // Без этого `.catch` брошенный поздний отказ всплыл бы unhandled
    // rejection в проде.
    void networkPromise
      .then((got) => applyFetchResult(args.store, args.teamId, args.songTextId, got))
      .catch(() => { /* опоздавший отказ сети — показывать уже нечему */ });
    return { source: 'cache', payload: cached.payload as P };
  }

  // Кэша тоже нет — сочинять отказ раньше времени нечестно: ждём НАСТОЯЩИЙ
  // исход сети, других данных всё равно неоткуда взять.
  const got = await networkPromise;
  return applyFetchResult(args.store, args.teamId, args.songTextId, got);
}

/**
 * Команда и версия публикации слов — из кэшированного снимка библиотеки.
 *
 * Страница `/words/<id>` открывается прямой ссылкой и про команду не знает
 * ничего: `GET /api/song-texts/:id` `teamId` не возвращает, а адрес несёт
 * только id публикации. Снимок библиотеки его знает — он же и есть источник
 * `updatedAt`, по которому сверяется докачка (offline/prefetch.ts). Писать в
 * кэш другую версию значило бы заставить докачку тянуть эту песню заново на
 * каждом заходе в библиотеку.
 *
 * `null` — «команда неизвестна»: снимка нет (первый запуск, приватный режим)
 * или песни в нём уже нет. Кэша для неё в таком случае всё равно нет, и
 * потребитель честно уходит в сеть.
 */
export function findSongTextInCache(
  teams: readonly TeamLibrary[] | null,
  songTextId: string,
): { teamId: string; updatedAt: string } | null {
  for (const team of teams ?? []) {
    for (const st of team.songTexts ?? []) {
      if (st.id === songTextId) return { teamId: team.teamId, updatedAt: st.updatedAt };
    }
  }
  return null;
}

export interface ResolveWordsForPageArgs<P> {
  /** `null` — хранилища нет (приватный режим, вошедший неизвестен). */
  store: OfflineStore | null;
  songTextId: string;
  /** Тот же исход, что отдаёт `fetchSongText`, — без `updatedAt`: маршрут
   *  слов его не возвращает, версию приносит снимок библиотеки. */
  fetchOne: () => Promise<{ ok: true; payload: P } | { ok: false; reason: WordsFetchReason }>;
  /** Пробрасывается в `resolveWords` без изменений — см. его doc-комментарий
   *  (дефект 1 живой приёмки волны G). Не задано — тот же default там. */
  isOffline?: () => boolean;
}

/**
 * Готовый ответ для страницы слов: сам находит команду и версию, сам решает,
 * участвует ли кэш вообще. Проводке (`main.ts`) остаётся один вызов — ни
 * одного правила в монолите, ровно как у библиотеки (offline/apply-sync.ts).
 */
export async function resolveWordsForPage<P>(args: ResolveWordsForPageArgs<P>): Promise<WordsResolved<P>> {
  const store = args.store;
  const found = store
    ? findSongTextInCache((await store.loadLibrary())?.teams ?? null, args.songTextId)
    : null;

  if (!store || !found) {
    const r = await args.fetchOne();
    return r.ok ? { source: 'network', payload: r.payload } : { source: 'none', reason: r.reason };
  }

  return resolveWords<P>({
    store,
    teamId: found.teamId,
    songTextId: args.songTextId,
    isOffline: args.isOffline,
    fetchOne: async () => {
      const r = await args.fetchOne();
      return r.ok ? { ok: true, payload: r.payload, updatedAt: found.updatedAt } : r;
    },
  });
}
